import React from 'react';

/**
 * Card component with multiple variants for different use cases
 * Variants: default, feature, phase, testimonial, stat, challenge
 * Color variants: purple, emerald, coral, amber, indigo, cyan
 */
const Card = ({ 
  children, 
  className = '',
  variant = 'default',
  colorVariant = null,
  hover = false,
  padding = 'default',
  gradient = false,
  borderAccent = false,
  ...props 
}) => {
  const baseStyles = 'bg-[var(--surface-card)] rounded-3xl shadow-[0_12px_30px_rgba(20,48,36,0.08)] border border-[rgba(31,42,36,0.08)] transition-all duration-200';
  const hoverStyles = hover ? 'hover:shadow-[0_18px_38px_rgba(20,48,36,0.12)] hover:-translate-y-1' : '';
  
  const variants = {
    default:  'bg-[var(--surface-card)]',
    feature:  'bg-[var(--surface-card)]',
    phase:    'bg-[var(--surface-soft-2)]',
    testimonial: 'bg-[var(--surface-soft-1)]',
    stat:     'bg-[var(--surface-card)]',
    challenge: 'bg-[var(--surface-soft-1)] backdrop-blur text-[var(--text-primary)]',
  };
  
  // Vibrant color variants with gradients
  const colorVariants = {
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200',
    emerald: 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200',
    coral: 'bg-gradient-to-br from-coral-50 to-coral-100/50 border-coral-200',
    amber: 'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200',
    indigo: 'bg-gradient-to-br from-indigo-50 to-indigo-100/50 border-indigo-200',
    cyan: 'bg-gradient-to-br from-cyan-50 to-cyan-100/50 border-cyan-200',
    teal: 'bg-gradient-to-br from-teal-50 to-teal-100/50 border-teal-200',
    violet: 'bg-gradient-to-br from-violet-50 to-violet-100/50 border-violet-200',
  };
  
  // Border accent colors
  const borderAccents = {
    purple: 'border-l-4 border-l-purple-500',
    emerald: 'border-l-4 border-l-emerald-500',
    coral: 'border-l-4 border-l-coral-500',
    amber: 'border-l-4 border-l-amber-500',
    indigo: 'border-l-4 border-l-indigo-600',
    cyan: 'border-l-4 border-l-cyan-500',
    teal: 'border-l-4 border-l-teal-500',
  };
  
  const paddingStyles = {
    none: '',
    sm: 'p-4',
    default: 'p-6',
    lg: 'p-8',
  };
  
  const gradientOverlay = gradient ? (
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-50 rounded-lg`} />
  ) : null;
  
  const selectedVariant = colorVariant ? colorVariants[colorVariant] : variants[variant];
  const accentBorder = borderAccent && colorVariant ? borderAccents[colorVariant] : '';
  
  return (
    <div 
      className={`relative overflow-hidden ${baseStyles} ${hoverStyles} ${selectedVariant} ${accentBorder} ${paddingStyles[padding]} ${className}`}
      {...props}
    >
      {gradientOverlay}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default Card;
